import React from 'react';
import classNames from 'classnames';
import {
  TextField,
  Stack,
  ChoiceList,
  Heading,
  TextContainer,
  Button,
  Select,
} from '@shopify/polaris';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {FiltersContainer} from 'sections/services-marketplace/experts/components';
import {BrowseExpertsFilters, PolarisChoice} from 'sections/types';
import {Profile} from 'sections/services-marketplace/experts/utilities';

import styles from './FilterInputs.scss';

export interface OwnProps {
  filterState: BrowseExpertsFilters;
  locations: PolarisChoice[];
  filterChangeHandlers: {
    onPriceRangeChange(key: string): (value: string) => void;
    onLocationSelect(value: string, id: string): void;
    onLanguageChange(value: string[]): void;
    onSubmitFilters(): void;
    onResetFilters(): void;
  };
}

type CombinedProps = OwnProps & WithI18nProps;

interface State {
  languagesExpanded: boolean;
}

class FilterInputs extends React.Component<CombinedProps, State> {
  languageOptions = Object.keys(Profile.getLanguageMap()).map(key => ({
    value: key,
    label: Profile.getLanguageLabel(key),
  }));

  state: State = {
    languagesExpanded: false,
  };

  onToggleLanguages = (): void => {
    this.setState(({languagesExpanded}) => ({
      languagesExpanded: !languagesExpanded,
    }));
  };

  render() {
    const {
      filterState: {minPrice, maxPrice, languages, location},
      filterChangeHandlers: {
        onPriceRangeChange,
        onLocationSelect,
        onLanguageChange,
        onSubmitFilters,
        onResetFilters,
      },
      locations,
      i18n,
    } = this.props;
    const {languagesExpanded} = this.state;

    const PriceFilter = (
      <TextContainer spacing="tight">
        <span>{i18n.translate('ExpertIndex.Filters.priceRangeLabel')}</span>
        <div className={styles.PriceInputs}>
          <div className={styles.PriceInput}>
            <TextField
              label={i18n.translate('ExpertIndex.Filters.minPriceLabel')}
              labelHidden
              type="number"
              placeholder={i18n.translate(
                'ExpertIndex.Filters.minPricePlaceholder'
              )}
              min={0}
              step={100}
              value={minPrice && minPrice}
              onChange={onPriceRangeChange('minPrice')}
            />
          </div>
          <div className={styles.PriceInput}>
            <TextField
              label={i18n.translate('ExpertIndex.Filters.maxPriceLabel')}
              labelHidden
              type="number"
              placeholder={i18n.translate(
                'ExpertIndex.Filters.maxPricePlaceholder'
              )}
              min={0}
              step={100}
              value={maxPrice && maxPrice}
              onChange={onPriceRangeChange('maxPrice')}
            />
          </div>
        </div>
      </TextContainer>
    );

    const LanguageFilter = (
      <div
        className={classNames(
          styles.LanguageInput,
          languagesExpanded && styles.LanguageInputExpanded
        )}
      >
        <ChoiceList
          allowMultiple
          title={i18n.translate('ExpertIndex.Filters.languagesLabel')}
          choices={this.languageOptions}
          selected={languages || []}
          onChange={onLanguageChange}
        />
        <Button plain onClick={this.onToggleLanguages}>
          {languagesExpanded
            ? i18n.translate('ExpertIndex.Filters.seeLess')
            : i18n.translate('ExpertIndex.Filters.seeAll')}
        </Button>
      </div>
    );

    const LocationFilter = (
      <Select
        label={i18n.translate('ExpertIndex.Filters.locationLabel')}
        options={locations}
        onChange={onLocationSelect}
        value={location}
        placeholder={i18n.translate('ExpertIndex.Filters.locationPlaceholder')}
      />
    );

    return (
      <FiltersContainer
        onSubmit={onSubmitFilters}
        onReset={onResetFilters}
        buttonText={i18n.translate('ExpertIndex.Filters.searchLabel')}
        showDesktopActions
      >
        <Stack vertical>
          <Heading>
            <span className={styles.FiltersTitle}>
              {i18n.translate('ExpertIndex.Filters.title')}
            </span>
          </Heading>
          <Stack vertical spacing="loose">
            {PriceFilter}
            {LocationFilter}
            {LanguageFilter}
          </Stack>
        </Stack>
      </FiltersContainer>
    );
  }
}

export default withI18n()(FilterInputs);
